import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Minus, Plus } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { DocumentPreview, type PrintZone } from "@/components/print/DocumentPreview";
import { Button, useMediaQuery } from "@akuna/ui";
import type { PreviewVariant } from "@/lib/print/previewHtml";
import { cn } from "@/lib/utils";
import type { PrintScope } from "./printLayoutForm";

type Props = {
  html: string;
  scope: PrintScope;
  variant: PreviewVariant;
  onVariantChange: (variant: PreviewVariant) => void;
  activeZone: PrintZone;
  onZoneClick: (zone: PrintZone) => void;
  /** Mobile layout: no title, scrolling variant bar, capped sheet height and a full-page drawer. */
  compact?: boolean;
};

const VARIANTS: { value: PreviewVariant; key: string }[] = [
  { value: "simples", key: "simple" },
  { value: "controle", key: "controlled" },
  { value: "digital", key: "digital" },
];
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 1.2;
const ZOOM_STEP = 0.1;

export function PreviewCard({ html, scope, variant, onVariantChange, activeZone, onZoneClick, compact = false }: Props) {
  const { t } = useTranslation("settings");
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [zoom, setZoom] = useState(0.9);
  const [fullOpen, setFullOpen] = useState(false);
  const clamp = (n: number) => Math.round(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, n)) * 100) / 100;

  const variantBar =
    scope === "prescription" ? (
      <>
        <span className="shrink-0 text-[12.5px] text-muted-foreground">{t("printEditor.preview.viewWith")}</span>
        <div
          role="group"
          aria-label={t("printEditor.preview.viewWith")}
          className="flex gap-1 rounded-full bg-secondary p-[3px]"
        >
          {VARIANTS.map(({ value, key }) => (
            <button
              key={value}
              type="button"
              aria-pressed={variant === value}
              onClick={() => onVariantChange(value)}
              className={cn(
                "shrink-0 rounded-full px-3 py-[7px] text-[12.5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                compact && "min-h-11",
                variant === value ? "bg-card font-semibold shadow-akuna-xs" : "text-muted-foreground",
              )}
            >
              {t(`printEditor.preview.${key}`)}
            </button>
          ))}
        </div>
      </>
    ) : null;

  if (compact) {
    return (
      <Card className="min-w-0 overflow-hidden">
        {variantBar ? (
          <div className="flex items-center gap-2 overflow-x-auto border-b border-border/60 px-4 py-2.5">{variantBar}</div>
        ) : null}
        <div className="flex flex-col gap-2.5 bg-print-workspace px-4 py-3">
          <div className="max-h-[300px] overflow-hidden rounded-md">
            <DocumentPreview
              html={html}
              title={t("printEditor.preview.title")}
              scale="fit"
              activeZone={activeZone}
              onZoneClick={onZoneClick}
            />
          </div>
          <Button type="button" variant="outline" size="sm" className="min-h-11 self-center" onClick={() => setFullOpen(true)}>
            {t("printEditor.preview.fullPage")}
          </Button>
        </div>
        <Drawer open={fullOpen} onOpenChange={setFullOpen}>
          <DrawerContent className="max-h-[92dvh]">
            <DrawerHeader>
              <DrawerTitle>{t("printEditor.preview.title")}</DrawerTitle>
              <DrawerDescription className="sr-only">{t("printEditor.preview.fullPage")}</DrawerDescription>
            </DrawerHeader>
            <div className="overflow-y-auto bg-print-workspace px-4 pb-6 pt-3">
              <DocumentPreview html={html} title={t("printEditor.preview.title")} scale="fit" />
            </div>
          </DrawerContent>
        </Drawer>
      </Card>
    );
  }

  return (
    <Card className="min-w-0 flex-1 overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 border-b border-border/60 px-5 py-4">
        <h2 className="mr-2 text-lg font-semibold">{t("printEditor.preview.title")}</h2>
        {scope === "prescription" ? (
          <>
            <span className="text-[12.5px] text-muted-foreground">{t("printEditor.preview.viewWith")}</span>
            <div role="group" aria-label={t("printEditor.preview.viewWith")} className="flex gap-1 rounded-full bg-secondary p-[3px]">
              {VARIANTS.map(({ value, key }) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={variant === value}
                  onClick={() => onVariantChange(value)}
                  className={cn(
                    "rounded-full px-3 py-[7px] text-[12.5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    variant === value ? "bg-card font-semibold shadow-akuna-xs" : "text-muted-foreground",
                  )}
                >
                  {t(`printEditor.preview.${key}`)}
                </button>
              ))}
            </div>
          </>
        ) : null}
        {isDesktop ? (
          <div className="ml-auto flex items-center rounded-[9px] border bg-card">
            <button
              type="button"
              aria-label={t("printEditor.preview.zoomOut")}
              onClick={() => setZoom((z) => clamp(z - ZOOM_STEP))}
              className="p-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Minus className="h-4 w-4" />
            </button>
            <span aria-live="polite" className="min-w-[46px] text-center text-[12.5px]">
              {Math.round(zoom * 100)}%
            </span>
            <button
              type="button"
              aria-label={t("printEditor.preview.zoomIn")}
              onClick={() => setZoom((z) => clamp(z + ZOOM_STEP))}
              className="p-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        ) : null}
      </div>
      <div className="flex justify-center bg-print-workspace px-5 pb-8 pt-7">
        <DocumentPreview
          html={html}
          title={t("printEditor.preview.title")}
          scale={isDesktop ? zoom : "fit"}
          activeZone={activeZone}
          onZoneClick={onZoneClick}
        />
      </div>
    </Card>
  );
}
